import { useState, useRef, useEffect } from "react";
import { useApp } from "@/App";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar } from "@/components/ui/calendar";
import { DayPicker } from "react-day-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Check, Plus, ChevronRight, ChevronLeft,
  Clock, Sparkles, AlertCircle, Wind, Snowflake, Settings,
  Lightbulb, Droplets, Armchair, Circle, Minus
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import { cs } from "date-fns/locale";

const SedanIcon = ({ className }) => (
  <svg viewBox="0 0 80 40" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <path d="M16 30c0-3.3 2.7-6 6-6s6 2.7 6 6M52 30c0-3.3 2.7-6 6-6s6 2.7 6 6" stroke="currentColor" strokeWidth="2"/>
    <circle cx="22" cy="30" r="3" fill="currentColor"/>
    <circle cx="58" cy="30" r="3" fill="currentColor"/>
    <path d="M10 28h8M62 28h8c1 0 2-1 2-2v-4c0-1-.5-2-1.5-2.5L64 16l-8-6c-2-1.5-6-3-12-3H34c-6 0-10 1.5-12 3l-8 6-6.5 3.5C6.5 20 6 21 6 22v4c0 1 1 2 2 2h2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M22 10c4-2 8-3 12-3h2c4 0 8 1 12 3l6 5H16l6-5z" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.06"/>
    <line x1="40" y1="7" x2="40" y2="15" stroke="currentColor" strokeWidth="1" opacity="0.3"/>
    <path d="M6 22h68" stroke="currentColor" strokeWidth="0.5" opacity="0.2"/>
  </svg>
);

const SuvIcon = ({ className }) => (
  <svg viewBox="0 0 80 40" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <path d="M14 30c0-3.9 3.1-7 7-7s7 3.1 7 7M52 30c0-3.9 3.1-7 7-7s7 3.1 7 7" stroke="currentColor" strokeWidth="2"/>
    <circle cx="21" cy="30" r="3.5" fill="currentColor"/>
    <circle cx="59" cy="30" r="3.5" fill="currentColor"/>
    <path d="M8 28h8M64 28h8c1 0 2-1 2-2v-6c0-1.5-1-3-2-3.5L66 13l-6-5c-3-2-7-3-14-3H34c-7 0-11 1-14 3l-6 5-6 3.5c-1 .5-2 2-2 3.5v6c0 1 1 2 2 2h0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M20 8c4-2 9-3 14-3h12c5 0 10 1 14 3l5 4.5H15L20 8z" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.06"/>
    <line x1="36" y1="5" x2="36" y2="12.5" stroke="currentColor" strokeWidth="1" opacity="0.3"/>
    <path d="M4 20h72" stroke="currentColor" strokeWidth="0.5" opacity="0.2"/>
    <rect x="12" y="18" width="4" height="2" rx="0.5" fill="currentColor" opacity="0.4"/>
    <rect x="64" y="18" width="4" height="2" rx="0.5" fill="currentColor" opacity="0.4"/>
  </svg>
);

const VanIcon = ({ className }) => (
  <svg viewBox="0 0 80 40" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <path d="M12 30c0-3.3 2.7-6 6-6s6 2.7 6 6M54 30c0-3.3 2.7-6 6-6s6 2.7 6 6" stroke="currentColor" strokeWidth="2"/>
    <circle cx="18" cy="30" r="3" fill="currentColor"/>
    <circle cx="60" cy="30" r="3" fill="currentColor"/>
    <path d="M6 28h8M64 28h10c1 0 2-1 2-2V10c0-2-1-4-3-5-3-1-7-2-13-2H28c-6 0-11 .5-14 2-2 1-4 3-5 5L5 17c-1 1-1 2-1 3v6c0 1 1 2 2 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="44" y1="3" x2="44" y2="28" stroke="currentColor" strokeWidth="1" opacity="0.2"/>
    <path d="M4 18h72" stroke="currentColor" strokeWidth="0.5" opacity="0.2"/>
    <rect x="48" y="7" width="12" height="8" rx="1" stroke="currentColor" strokeWidth="1" opacity="0.3"/>
    <rect x="14" y="9" width="10" height="6" rx="1" stroke="currentColor" strokeWidth="1" opacity="0.3"/>
  </svg>
);

const iconMap = {
  wind: Wind,
  snowflake: Snowflake,
  settings: Settings,
  lightbulb: Lightbulb,
  droplets: Droplets,
  armchair: Armchair,
  circle: Circle,
  minus: Minus,
  sparkles: Sparkles,
};

const vehicleTypes = [
  { id: "sedan", label: "Sedan / Hatchback", icon: SedanIcon, priceMultiplier: 1 },
  { id: "suv", label: "SUV / Kombi", icon: SuvIcon, priceMultiplier: 1.2 },
  { id: "van", label: "Dodávka / MPV", icon: VanIcon, priceMultiplier: 1.3 },
];

const timeSlots = [
  "08:00", "09:00", "10:00", "11:00",
  "12:00", "13:00", "14:00", "15:00", "16:00", "17:00"
];

export default function BookingWizard() {
  const { packages, addons, selectedLocation, API } = useApp();
  const [step, setStep] = useState(0);
  const [vehicleType, setVehicleType] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [customerInfo, setCustomerInfo] = useState({
    name: "", phone: "", email: "", brand: "", notes: ""
  });
  const [availableSlots, setAvailableSlots] = useState(timeSlots);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [discountCode, setDiscountCode] = useState("");
  const [discountResult, setDiscountResult] = useState(null);
  const [discountLoading, setDiscountLoading] = useState(false);
  const [gdprConsent, setGdprConsent] = useState(false);
  const wizardRef = useRef(null);

  useEffect(() => {
    if (wizardRef.current) {
      wizardRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [step]);

  const steps = [
    "Typ vozidla",
    "Balíček",
    "Doplňky",
    "Termín",
    "Kontakt"
  ];

  const multiplier = vehicleType ? vehicleTypes.find(v => v.id === vehicleType)?.priceMultiplier || 1 : 1;

  const calcTotal = () => {
    let total = selectedPackage ? Math.round(selectedPackage.price * multiplier) : 0;
    selectedAddons.forEach(a => { total += a.price; });
    return total;
  };

  const finalPrice = () => {
    const total = calcTotal();
    if (discountResult?.valid) return Math.max(0, total - discountResult.discount_amount);
    return total;
  };

  const validateDiscount = async () => {
    if (!discountCode.trim()) return;
    setDiscountLoading(true);
    try {
      const res = await axios.post(`${API}/discount/validate`, {
        code: discountCode.trim(),
        total: calcTotal(),
        vehicle_type: vehicleType || "sedan"
      });
      setDiscountResult(res.data);
      if (res.data.valid) {
        toast.success(`Slevový kód aplikován: -${res.data.discount_amount} Kč`);
      } else {
        toast.error("Neplatný slevový kód");
      }
    } catch {
      toast.error("Chyba při ověřování kódu");
      setDiscountResult(null);
    } finally {
      setDiscountLoading(false);
    }
  };

  const toggleAddon = (addon) => {
    setSelectedAddons(prev =>
      prev.find(a => a.id === addon.id)
        ? prev.filter(a => a.id !== addon.id)
        : [...prev, addon]
    );
  };

  const fetchSlots = async (date) => {
    try {
      const dateStr = format(date, "yyyy-MM-dd");
      const res = await axios.get(`${API}/timeslots`, {
        params: { date: dateStr, location: selectedLocation }
      });
      setAvailableSlots(res.data.available);
    } catch {
      setAvailableSlots(timeSlots);
    }
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedTime(null);
    if (date) fetchSlots(date);
  };

  const canNext = () => {
    switch (step) {
      case 0: return !!vehicleType;
      case 1: return !!selectedPackage;
      case 2: return true;
      case 3: return !!selectedDate && !!selectedTime;
      case 4: return customerInfo.name && customerInfo.phone && customerInfo.email && gdprConsent;
      default: return false;
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, {
        location: selectedLocation,
        customer_name: customerInfo.name,
        customer_phone: customerInfo.phone,
        customer_email: customerInfo.email,
        vehicle_type: vehicleType,
        vehicle_brand: customerInfo.brand,
        package_id: selectedPackage.id,
        package_name: selectedPackage.name,
        addon_ids: selectedAddons.map(a => a.id),
        addon_names: selectedAddons.map(a => a.name),
        date: format(selectedDate, "yyyy-MM-dd"),
        time: selectedTime,
        total_price: calcTotal(),
        discount_code: discountResult?.valid ? discountCode.trim().toUpperCase() : "",
        discount_amount: discountResult?.valid ? discountResult.discount_amount : 0,
        final_price: finalPrice(),
        gdpr_consent: gdprConsent,
        notes: customerInfo.notes
      });
      setCompleted(true);
      toast.success("Rezervace byla úspěšně vytvořena! Potvrzení odesláno na e-mail.");
    } catch (e) {
      toast.error("Nastala chyba při vytváření rezervace.");
    } finally {
      setSubmitting(false);
    }
  };

  if (completed) {
    return (
      <section className="py-24 md:py-32 bg-[#050505]" data-testid="booking-success">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", duration: 0.6 }}
            className="w-20 h-20 mx-auto mb-8 bg-[#39FF14] flex items-center justify-center"
          >
            <Check className="w-10 h-10 text-black" />
          </motion.div>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight uppercase mb-4">
            Rezervace <span className="text-[#39FF14]">potvrzena</span>
          </h2>
          <p className="text-zinc-400 mb-2">
            Děkujeme, {customerInfo.name}! Vaše rezervace na{" "}
            <span className="text-white font-semibold">
              {format(selectedDate, "d. MMMM yyyy", { locale: cs })}
            </span>{" "}
            v <span className="text-white font-semibold">{selectedTime}</span> byla přijata.
          </p>
          <p className="text-zinc-500 text-sm">
            Potvrzení jsme odeslali na {customerInfo.email}. Brzy se ozveme.
          </p>
          <div className="mt-8 p-6 bg-[#0A0A0A] border border-white/5 text-left">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#39FF14] mb-4">Souhrn</p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-zinc-500">Balíček</span><span>{selectedPackage.name}</span></div>
              {selectedAddons.length > 0 && (
                <div className="flex justify-between"><span className="text-zinc-500">Doplňky</span><span>{selectedAddons.map(a=>a.name).join(", ")}</span></div>
              )}
              {discountResult?.valid && (
                <div className="flex justify-between text-[#39FF14]"><span>Sleva ({discountCode.toUpperCase()})</span><span>-{discountResult.discount_amount} Kč</span></div>
              )}
              <div className="flex justify-between border-t border-white/5 pt-2 mt-2"><span className="text-zinc-500">Celkem</span><span className="text-[#39FF14] font-bold text-lg">{finalPrice().toLocaleString()} Kč</span></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-10 md:py-16 bg-[#050505]" data-testid="booking-section" ref={wizardRef}>
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#39FF14] mb-3">
            Rychlá rezervace
          </p>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight uppercase">
            Zarezervujte si <span className="text-[#39FF14]">termín</span>
          </h2>
          <p className="text-sm text-zinc-500 mt-2">Vyberte službu a rezervujte online — zabere to 2 minuty</p>
        </motion.div>

        {/* Step indicators */}
        <div className="flex items-center gap-2 mb-12 overflow-x-auto pb-2">
          {steps.map((s, i) => (
            <div key={i} className="flex items-center gap-2">
              <button
                onClick={() => i < step && setStep(i)}
                className={`w-8 h-8 flex items-center justify-center text-xs font-bold ${
                  i === step ? "step-active" : i < step ? "step-completed" : "step-inactive"
                }`}
                data-testid={`wizard-step-${i}`}
              >
                {i < step ? <Check className="w-3 h-3" /> : i + 1}
              </button>
              <span className={`text-xs tracking-wider uppercase whitespace-nowrap ${
                i === step ? "text-white font-bold" : "text-zinc-600"
              }`}>
                {s}
              </span>
              {i < steps.length - 1 && (
                <div className={`w-8 h-px ${i < step ? "bg-[#39FF14]/50" : "bg-white/10"}`} />
              )}
            </div>
          ))}
        </div>

        {/* Step content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {/* Step 0: Vehicle type */}
            {step === 0 && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="step-vehicle">
                {vehicleTypes.map((v) => {
                  const VIcon = v.icon;
                  return (
                    <button
                      key={v.id}
                      onClick={() => { setVehicleType(v.id); setTimeout(() => setStep(1), 300); }}
                      className={`p-8 border text-left transition-all duration-300 ${
                        vehicleType === v.id
                          ? "border-[#39FF14] bg-[#39FF14]/5"
                          : "border-white/10 bg-[#0A0A0A] hover:border-white/20"
                      }`}
                      data-testid={`vehicle-${v.id}`}
                    >
                      <VIcon className={`w-8 h-8 mb-4 ${vehicleType === v.id ? "text-[#39FF14]" : "text-zinc-500"}`} />
                      <h3 className="text-lg font-bold">{v.label}</h3>
                      {v.priceMultiplier > 1 && (
                        <p className="text-xs text-zinc-500 mt-2">
                          Příplatek +{Math.round((v.priceMultiplier - 1) * 100)}%
                        </p>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Step 1: Package selection */}
            {step === 1 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="step-package">
                {packages.map((pkg) => (
                  <button
                    key={pkg.id}
                    onClick={() => { setSelectedPackage(pkg); setTimeout(() => setStep(2), 300); }}
                    className={`p-6 border text-left transition-all duration-300 relative ${
                      selectedPackage?.id === pkg.id
                        ? "border-[#39FF14] bg-[#39FF14]/5"
                        : "border-white/10 bg-[#0A0A0A] hover:border-white/20"
                    }`}
                    data-testid={`package-${pkg.id}`}
                  >
                    {pkg.popular && (
                      <div className="absolute top-0 right-0 badge-recommended px-2 py-0.5 text-[10px]">
                        Doporučeno
                      </div>
                    )}
                    <h3 className="text-base font-bold uppercase mb-1">{pkg.name}</h3>
                    <p className="text-xs text-zinc-500 mb-3">{pkg.duration}</p>
                    <p className="text-xs text-zinc-400 mb-4">{pkg.description}</p>
                    <div className="flex items-end gap-1">
                      <span className="text-2xl font-black">{Math.round(pkg.price * multiplier).toLocaleString()}</span>
                      <span className="text-xs text-zinc-500 mb-1">Kč</span>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Step 2: Addons (upsell) */}
            {step === 2 && (
              <div data-testid="step-addons">
                <div className="bg-[#0A0A0A] border border-[#39FF14]/20 p-4 mb-6 flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-[#39FF14] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-bold text-white">Zákazníci také přidávají</p>
                    <p className="text-xs text-zinc-500">Doporučené doplňky pro maximální efekt péče o váš vůz</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {addons.map((addon) => {
                    const isSelected = selectedAddons.find(a => a.id === addon.id);
                    const AddonIcon = iconMap[addon.icon] || AlertCircle;
                    return (
                      <button
                        key={addon.id}
                        onClick={() => toggleAddon(addon)}
                        className={`addon-card p-5 border text-left flex items-start gap-4 transition-all duration-300 ${
                          isSelected
                            ? "selected border-[#39FF14] bg-[#39FF14]/5"
                            : "border-white/10 bg-[#0A0A0A] hover:border-white/20"
                        }`}
                        data-testid={`addon-${addon.id}`}
                      >
                        <div className={`w-10 h-10 flex items-center justify-center flex-shrink-0 ${
                          isSelected ? "bg-[#39FF14]/20" : "bg-white/5"
                        }`}>
                          {isSelected
                            ? <Check className="w-4 h-4 text-[#39FF14]" />
                            : <AddonIcon className="w-4 h-4 text-zinc-500" />
                          }
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className="text-sm font-bold">{addon.name}</h4>
                            {addon.recommended && (
                              <span className="badge-recommended text-[8px] px-1.5 py-0.5">
                                Top
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-zinc-500 mt-1">{addon.description}</p>
                        </div>
                        <span className="text-sm font-bold text-[#39FF14] whitespace-nowrap">
                          +{addon.price} Kč
                        </span>
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => setStep(3)}
                  className="mt-4 btn-skew bg-[#39FF14] text-black px-8 py-3 text-xs font-bold uppercase tracking-wider hover:bg-white transition-colors duration-300"
                  data-testid="addons-continue"
                >
                  <span>{selectedAddons.length > 0 ? `Pokračovat (${selectedAddons.length} vybráno)` : "Pokračovat bez doplňků"}</span>
                </button>
              </div>
            )}

            {/* Step 3: Date & Time */}
            {step === 3 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-testid="step-datetime">
                <div>
                  <Label className="text-xs font-bold tracking-[0.2em] uppercase text-[#39FF14] mb-4 block">
                    Datum
                  </Label>
                  <div className="bg-[#0A0A0A] border border-white/10 p-4 inline-block calendar-dark">
                    <DayPicker
                      mode="single"
                      selected={selectedDate}
                      onSelect={(day) => handleDateSelect(day)}
                      locale={cs}
                      disabled={(date) => {
                        const today = new Date();
                        today.setHours(0,0,0,0);
                        const day = date.getDay();
                        return date < today || day === 0 || day === 6;
                      }}
                      showOutsideDays
                      className="p-3 text-white"
                      data-testid="booking-calendar"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-bold tracking-[0.2em] uppercase text-[#39FF14] mb-4 block">
                    Čas
                  </Label>
                  {selectedDate ? (
                    <div className="grid grid-cols-2 gap-2">
                      {availableSlots.map((slot) => (
                        <button
                          key={slot}
                          onClick={() => { setSelectedTime(slot); setTimeout(() => setStep(4), 300); }}
                          className={`p-3 border text-sm font-medium flex items-center justify-center gap-2 transition-all duration-300 ${
                            selectedTime === slot
                              ? "border-[#39FF14] bg-[#39FF14]/10 text-[#39FF14]"
                              : "border-white/10 bg-[#0A0A0A] text-zinc-400 hover:border-white/20"
                          }`}
                          data-testid={`time-${slot}`}
                        >
                          <Clock className="w-3 h-3" />
                          {slot}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-zinc-600 py-8">
                      <AlertCircle className="w-4 h-4" />
                      <span className="text-sm">Nejprve vyberte datum</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 4: Contact info */}
            {step === 4 && (
              <div className="max-w-lg" data-testid="step-contact">
                <div className="space-y-5">
                  <div>
                    <Label className="text-xs font-bold tracking-[0.15em] uppercase text-zinc-400 mb-2 block">
                      Jméno a příjmení *
                    </Label>
                    <Input
                      value={customerInfo.name}
                      onChange={(e) => setCustomerInfo(p => ({...p, name: e.target.value}))}
                      className="bg-white/5 border-white/10 focus:border-[#39FF14] text-white placeholder:text-zinc-600 rounded-none h-12"
                      placeholder="Jan Novák"
                      data-testid="input-name"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold tracking-[0.15em] uppercase text-zinc-400 mb-2 block">
                      Telefon *
                    </Label>
                    <Input
                      value={customerInfo.phone}
                      onChange={(e) => setCustomerInfo(p => ({...p, phone: e.target.value}))}
                      className="bg-white/5 border-white/10 focus:border-[#39FF14] text-white placeholder:text-zinc-600 rounded-none h-12"
                      placeholder="+420 777 123 456"
                      data-testid="input-phone"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold tracking-[0.15em] uppercase text-zinc-400 mb-2 block">
                      E-mail *
                    </Label>
                    <Input
                      value={customerInfo.email}
                      onChange={(e) => setCustomerInfo(p => ({...p, email: e.target.value}))}
                      type="email"
                      className="bg-white/5 border-white/10 focus:border-[#39FF14] text-white placeholder:text-zinc-600 rounded-none h-12"
                      placeholder="jan@email.cz"
                      data-testid="input-email"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold tracking-[0.15em] uppercase text-zinc-400 mb-2 block">
                      Značka a model vozidla
                    </Label>
                    <Input
                      value={customerInfo.brand}
                      onChange={(e) => setCustomerInfo(p => ({...p, brand: e.target.value}))}
                      className="bg-white/5 border-white/10 focus:border-[#39FF14] text-white placeholder:text-zinc-600 rounded-none h-12"
                      placeholder="BMW 3 Series"
                      data-testid="input-brand"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold tracking-[0.15em] uppercase text-zinc-400 mb-2 block">
                      Poznámka
                    </Label>
                    <textarea
                      value={customerInfo.notes}
                      onChange={(e) => setCustomerInfo(p => ({...p, notes: e.target.value}))}
                      className="w-full bg-white/5 border border-white/10 focus:border-[#39FF14] focus:outline-none text-white placeholder:text-zinc-600 rounded-none p-3 h-24 text-sm"
                      placeholder="Doplňující informace..."
                      data-testid="input-notes"
                    />
                  </div>

                  {/* Discount code */}
                  <div>
                    <Label className="text-xs font-bold tracking-[0.15em] uppercase text-zinc-400 mb-2 block">
                      Slevový kód
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        value={discountCode}
                        onChange={(e) => { setDiscountCode(e.target.value); setDiscountResult(null); }}
                        className="bg-white/5 border-white/10 focus:border-[#39FF14] text-white placeholder:text-zinc-600 rounded-none h-12 uppercase"
                        placeholder="Zadejte kód"
                        data-testid="input-discount"
                      />
                      <button
                        type="button"
                        onClick={validateDiscount}
                        disabled={!discountCode.trim() || discountLoading}
                        className={`px-6 h-12 text-xs font-bold uppercase tracking-wider whitespace-nowrap transition-colors duration-300 ${
                          discountCode.trim() && !discountLoading
                            ? "bg-[#39FF14] text-black hover:bg-white"
                            : "bg-white/10 text-zinc-600 cursor-not-allowed"
                        }`}
                        data-testid="btn-validate-discount"
                      >
                        {discountLoading ? "..." : "Ověřit"}
                      </button>
                    </div>
                    {discountResult && (
                      <p className={`text-xs mt-2 ${discountResult.valid ? "text-[#39FF14]" : "text-red-400"}`}>
                        {discountResult.valid
                          ? `${discountResult.description} (sleva ${discountResult.discount_amount} Kč)`
                          : "Neplatný slevový kód"
                        }
                      </p>
                    )}
                  </div>

                  {/* GDPR consent */}
                  <div className="pt-4 border-t border-white/5">
                    <label className="flex items-start gap-3 cursor-pointer group" data-testid="gdpr-consent-label">
                      <div
                        className={`w-5 h-5 mt-0.5 flex-shrink-0 flex items-center justify-center border transition-colors duration-300 ${
                          gdprConsent
                            ? "bg-[#39FF14] border-[#39FF14]"
                            : "border-white/20 group-hover:border-white/40"
                        }`}
                        onClick={() => setGdprConsent(!gdprConsent)}
                        data-testid="gdpr-checkbox"
                      >
                        {gdprConsent && <Check className="w-3 h-3 text-black" />}
                      </div>
                      <span className="text-xs text-zinc-400 leading-relaxed" onClick={() => setGdprConsent(!gdprConsent)}>
                        Souhlasím se zpracováním osobních údajů v souladu s nařízením GDPR (EU) 2016/679.
                        Vaše údaje (jméno, telefon, e-mail) budou použity výhradně pro účely realizace
                        rezervace a komunikaci ohledně objednaných služeb. Údaje nebudou předány třetím
                        stranám a budou uchovány po dobu nezbytně nutnou. Svůj souhlas můžete kdykoliv
                        odvolat na e-mailu info@automyckacestlice.cz. *
                      </span>
                    </label>
                  </div>
                </div>

                {/* Summary */}
                <div className="mt-8 p-6 bg-[#0A0A0A] border border-white/5">
                  <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#39FF14] mb-4">Souhrn objednávky</p>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Vozidlo</span>
                      <span>{vehicleTypes.find(v=>v.id===vehicleType)?.label}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Balíček</span>
                      <span>{selectedPackage?.name} — {Math.round(selectedPackage?.price * multiplier).toLocaleString()} Kč</span>
                    </div>
                    {selectedAddons.map(a => (
                      <div key={a.id} className="flex justify-between">
                        <span className="text-zinc-500">+ {a.name}</span>
                        <span>{a.price} Kč</span>
                      </div>
                    ))}
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Termín</span>
                      <span>{selectedDate && format(selectedDate, "d.M.yyyy")} v {selectedTime}</span>
                    </div>
                    {discountResult?.valid && (
                      <div className="flex justify-between text-[#39FF14]">
                        <span>Sleva ({discountCode.toUpperCase()})</span>
                        <span>-{discountResult.discount_amount} Kč</span>
                      </div>
                    )}
                    <div className="flex justify-between border-t border-white/5 pt-3 mt-3">
                      <span className="font-bold">Celkem</span>
                      <span className="text-[#39FF14] font-black text-xl">{finalPrice().toLocaleString()} Kč</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Navigation buttons */}
        <div className="flex items-center justify-between mt-10 pt-8 border-t border-white/5">
          <div>
            {step > 0 && (
              <button
                onClick={() => setStep(s => s - 1)}
                className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors duration-300"
                data-testid="wizard-back"
              >
                <ChevronLeft className="w-4 h-4" />
                Zpět
              </button>
            )}
          </div>

          <div className="flex items-center gap-4">
            {/* Running total */}
            {(selectedPackage || selectedAddons.length > 0) && (
              <div className="text-right mr-4">
                <p className="text-xs text-zinc-500">Celkem</p>
                <p className="text-lg font-black text-[#39FF14]">{finalPrice().toLocaleString()} Kč</p>
              </div>
            )}

            {step < steps.length - 1 ? (
              <button
                onClick={() => canNext() && setStep(s => s + 1)}
                disabled={!canNext()}
                className={`btn-skew px-8 py-3 text-xs font-bold uppercase tracking-wider flex items-center gap-2 transition-colors duration-300 ${
                  canNext()
                    ? "bg-[#39FF14] text-black hover:bg-white"
                    : "bg-white/10 text-zinc-600 cursor-not-allowed"
                }`}
                data-testid="wizard-next"
              >
                <span className="flex items-center gap-2">
                  Další <ChevronRight className="w-4 h-4" />
                </span>
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={!canNext() || submitting}
                className={`btn-skew px-10 py-3 text-xs font-bold uppercase tracking-wider transition-colors duration-300 ${
                  canNext() && !submitting
                    ? "bg-[#39FF14] text-black hover:bg-white animate-pulse-green"
                    : "bg-white/10 text-zinc-600 cursor-not-allowed"
                }`}
                data-testid="wizard-submit"
              >
                <span>{submitting ? "Odesílám..." : "Potvrdit rezervaci"}</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
